/**
 * PRV 입구 배관 압력손실 판정.
 *
 * 근거: API Standard 520 Part II, 6판(2015)
 *   - 7.3.4   비회복 입구 손실은 **설정압의 3 %** 를 넘지 않아야 한다.
 *             예외: 열팽창 밸브(7.3.8), 원격감지 파일럿(7.3.9),
 *                   engineering analysis 수행(7.3.6)
 *   - 7.3.7.2 손실에 포함할 것: 마찰(직관+부속), **용기→배관 입구 손실**,
 *             파열판이 있으면 그 압력강하.
 *             운동에너지와 액주 정압은 회복 가능하므로 제외.
 *   - 7.3.7.3 유량은 **정격용량(rated capacity)** 을 쓰는 것이 좋은 설계 관행.
 *             모듈레이팅 특성이 있는 밸브만 소요유량을 써도 된다.
 *   - 7.3.6 d) 3 % 를 넘을 때의 대안 — 힘 평형 판정(force balance)
 *
 * 압력강하 계산 자체는 `pipeFlow.ts`(일반 유체역학)가 맡는다.
 * 이 모듈은 **그 결과를 API 기준에 비추어 판정**한다.
 */
import { pressureDrop, type PipeSegment, type PressureDropResult } from "./pipeFlow";

/** 3 % 기준 (7.3.4). */
export const INLET_LOSS_LIMIT_PERCENT = 3;

/** 3 % 기준의 예외 (7.3.4). */
export type InletLossExemption =
  | "none"
  /** 열팽창 전용 밸브 — 7.3.8 */
  | "thermal-relief"
  /** 원격감지 파일럿형 — 7.3.9 */
  | "remote-sensed-pilot";

/** 유량 기준 (7.3.7.3). */
export type InletFlowBasis = "rated" | "required";

export interface InletLossInput {
  /** 설정압 (kPag) */
  readonly setPressureKpaGauge: number;
  /** 입구 배관 (직관 길이·내경·조도·부속 ΣK) */
  readonly segment: PipeSegment;
  /** 계산에 쓸 질량유량 (kg/s) */
  readonly massFlowKgS: number;
  /** 배출 조건에서의 밀도 (kg/m³) */
  readonly densityKgM3: number;
  /** 배출 조건에서의 절대점도 (Pa·s) */
  readonly viscosityPaS: number;
  readonly flowBasis: InletFlowBasis;
  readonly exemption?: InletLossExemption;
  /**
   * 파열판이 상류에 있을 때 그 압력강하 (kPa).
   * 7.3.7.2 b) 가 **반드시 더하라**고 한다.
   */
  readonly ruptureDiskDropKpa?: number;
}

/** 3 % 를 넘었을 때 쓰는 힘 평형 판정 입력 (7.3.6 d). */
export interface ForceBalanceInput {
  /** 총 입구 압력손실 (kPa) */
  readonly inletLossKpa: number;
  /** 축적배압 (kPa) */
  readonly builtUpBackpressureKpa: number;
  /** 허용 과압 (kPa). 표준은 이 판정에 **10 % 과압**을 권장한다. */
  readonly overpressureKpa: number;
  /** 블로다운 (kPa). 제작사 자료에서 얻는다. */
  readonly blowdownKpa: number;
  readonly valveType: "conventional" | "balanced-bellows";
}

export interface ForceBalanceResult {
  readonly passes: boolean;
  /** 판정식 좌변 (kPa) */
  readonly leftSide: number;
  /** 판정식 우변 (kPa) */
  readonly rightSide: number;
  readonly criterion: string;
  readonly warnings: readonly string[];
}

export interface InletLossSuccess {
  readonly ok: true;
  /** 배관 압력손실 (kPa) — 파열판 몫 제외 */
  readonly pipingLossKpa: number;
  /** 파열판 몫 (kPa) */
  readonly ruptureDiskLossKpa: number;
  /** 총 비회복 손실 (kPa) */
  readonly totalLossKpa: number;
  /** 설정압 대비 (%) */
  readonly lossPercent: number;
  /** 3 % 기준 통과 여부. 예외에 해당하면 항상 true. */
  readonly withinLimit: boolean;
  readonly exemption: InletLossExemption;
  readonly hydraulics: PressureDropResult;
  readonly warnings: readonly string[];
}

export interface InletLossFailure {
  readonly ok: false;
  readonly reason: "invalid-input";
  readonly message: string;
}

export type InletLossResult = InletLossSuccess | InletLossFailure;

function invalid(message: string): InletLossFailure {
  return { ok: false, reason: "invalid-input", message };
}

/** 입구 비회복 압력손실을 계산하고 3 % 기준으로 판정한다. */
export function checkInletLoss(input: InletLossInput): InletLossResult {
  const {
    setPressureKpaGauge: Pset,
    segment,
    massFlowKgS,
    densityKgM3,
    viscosityPaS,
  } = input;

  const positives: ReadonlyArray<readonly [string, number]> = [
    ["설정압", Pset],
    ["질량유량", massFlowKgS],
    ["밀도", densityKgM3],
    ["점도", viscosityPaS],
    ["직관 길이", segment.lengthM],
    ["내경", segment.innerDiameterM],
  ];
  for (const [label, v] of positives) {
    if (!Number.isFinite(v) || v <= 0) {
      return invalid(`${label} 은(는) 0보다 큰 유한한 값이어야 합니다.`);
    }
  }
  if (!Number.isFinite(segment.fittingK) || segment.fittingK < 0) {
    return invalid("부속 손실계수 ΣK 는 0 이상이어야 합니다.");
  }
  if (!Number.isFinite(segment.roughnessM) || segment.roughnessM < 0) {
    return invalid("배관 조도는 0 이상이어야 합니다.");
  }

  const hydraulics = pressureDrop(
    segment,
    massFlowKgS,
    densityKgM3,
    viscosityPaS,
  );
  const pipingLossKpa = hydraulics.pressureDropPa / 1000;
  const ruptureDiskLossKpa = input.ruptureDiskDropKpa ?? 0;
  const totalLossKpa = pipingLossKpa + ruptureDiskLossKpa;
  const lossPercent = (totalLossKpa / Pset) * 100;

  const exemption = input.exemption ?? "none";
  const warnings: string[] = [];

  if (input.flowBasis === "required") {
    warnings.push(
      "소요 유량 기준으로 계산했습니다. 표준은 정격용량(rated capacity) 기준을 " +
        "좋은 설계 관행으로 제시하며, 소요 유량은 모듈레이팅 특성이 확인된 밸브에만 " +
        "허용합니다(7.3.7.3). 제작사 확인이 필요합니다.",
    );
  }
  if (segment.fittingK === 0) {
    warnings.push(
      "부속 손실계수 ΣK 가 0 입니다. 용기에서 배관으로 들어가는 입구 손실은 " +
        "반드시 포함해야 합니다(7.3.7.2 a).",
    );
  }
  if (hydraulics.flowRegime === "transitional") {
    warnings.push(
      `레이놀즈수 ${hydraulics.reynolds.toFixed(0)} 은 층류·난류 천이영역이라 ` +
        "마찰계수가 불확실합니다. 결과를 보수적으로 해석하십시오.",
    );
  }

  let withinLimit = lossPercent <= INLET_LOSS_LIMIT_PERCENT;
  if (!withinLimit && exemption !== "none") {
    withinLimit = true;
    warnings.push(
      exemption === "thermal-relief"
        ? "열팽창 전용 밸브로 보아 3 % 기준을 적용하지 않았습니다(7.3.8). " +
          "공정열로 압력이 생길 수 있는 설비라면 이 예외를 쓸 수 없습니다."
        : "원격감지 파일럿형으로 보아 3 % 기준을 적용하지 않았습니다(7.3.9). " +
          "감지선이 규정대로 설치되어야 합니다.",
    );
  }

  return {
    ok: true,
    pipingLossKpa,
    ruptureDiskLossKpa,
    totalLossKpa,
    lossPercent,
    withinLimit,
    exemption,
    hydraulics,
    warnings,
  };
}

/**
 * 힘 평형 판정 — API 520 Part II 7.3.6 d).
 *
 * 3 % 를 넘었을 때 표준이 제시하는 **간이 대안**이다.
 *   일반형     : 총 입구손실 + 축적배압         ≤ 과압 + 블로다운
 *   벨로우즈   : 총 입구손실 + 0.1 × 축적배압  ≤ 과압 + 블로다운
 *
 * 벨로우즈에서 축적배압에 0.1 을 곱하는 이유: 벨로우즈가 디스크 면적의
 * 대부분(보수적으로 90 %)을 배압으로부터 차단하기 때문이다.
 *
 * ⚠ 표준이 명시하는 한계:
 *  - 등경(constant-diameter) 입구 배관에만 쓸 수 있다.
 *  - 좌변의 "총 입구 압력손실"에는 마찰 성분뿐 아니라 **파동(wave) 성분**도
 *    포함해야 한다. 이 앱은 마찰 성분만 계산하므로 그 점을 경고로 알린다.
 *  - **이미 채터가 발생한 설비를 이 판정으로 합격시킬 수 없다**(7.3.6).
 */
export function forceBalanceCheck(
  input: ForceBalanceInput,
): ForceBalanceResult {
  const bellows = input.valveType === "balanced-bellows";
  const leftSide =
    input.inletLossKpa +
    (bellows ? 0.1 : 1) * input.builtUpBackpressureKpa;
  const rightSide = input.overpressureKpa + input.blowdownKpa;

  return {
    passes: leftSide <= rightSide,
    leftSide,
    rightSide,
    criterion: bellows
      ? "총 입구손실 + 0.1 × 축적배압 ≤ 과압 + 블로다운"
      : "총 입구손실 + 축적배압 ≤ 과압 + 블로다운",
    warnings: [
      "이 판정의 좌변에는 마찰 성분뿐 아니라 파동(wave) 성분도 포함해야 " +
        "합니다(7.3.6 d). 이 앱은 마찰 성분만 계산하므로 결과가 낙관적일 수 있습니다.",
      "등경(constant-diameter) 입구 배관에만 적용할 수 있는 간이 방법입니다.",
      "이미 채터가 발생한 설비는 이 판정으로 합격시킬 수 없습니다(7.3.6).",
      "engineering analysis 의 세부 요건은 사용자 책임이며 문서화해야 합니다(7.3.6).",
    ],
  };
}
